import type { CalculatorBootstrap } from '../../engine/types';
import type { HsaSavingsValues } from './config';
import type { HsaSavingsResult } from './calculate';

/**
 * Optional HSA Savings modules (Brad, 2026-10-01), switched on per placement
 * by the Modules checkboxes in hsaSavingsCalc.yaml and passed in as
 * options.modules. Adding a module: a checkbox in the dialog, its name in
 * calculator-app.ftl's module list, a name here, and the layout(s) that
 * render it.
 */
export type HsaSavingsModule = 'balance-mix' | 'total-contributed';

export function hasModule(options: CalculatorBootstrap['options'], name: HsaSavingsModule): boolean {
  return Array.isArray(options.modules) && options.modules.includes(name);
}

export interface BalanceMix {
  /** Contributions minus medical expenses over the whole period (never below 0). */
  netDeposits: number;
  /** Final balance minus net deposits (never below 0). */
  earnings: number;
  /** Medical expenses over the whole period. */
  medicalExpenses: number;
  /** Share of the final balance that came from earnings, 0-1. */
  earningsShare: number;
}

const cents = (n: number) => Math.round(n * 100) / 100;

/**
 * "Ending balance mix" figures, derived only from the existing calculation
 * (calculate.ts is unchanged): the final balance is the yearly
 * (contribution - expenses) compounded, so everything above the plain
 * deposits is earnings. When expenses meet or exceed contributions
 * (result.noSavings) nothing accumulates, so deposits and earnings show 0.
 */
export function balanceMix(values: HsaSavingsValues, result: HsaSavingsResult): BalanceMix {
  const years = values.years ?? 0;
  const contribution = values.contributionAmt ?? 0;
  const expenses = values.medicalExpenses ?? 0;

  const netDeposits = cents(Math.max(0, (contribution - expenses) * years));
  const earnings = cents(Math.max(0, result.totalSavings - netDeposits));
  const total = netDeposits + earnings;

  return {
    netDeposits,
    earnings,
    medicalExpenses: totalMedicalExpenses(values),
    earningsShare: total > 0 ? earnings / total : 0,
  };
}

/**
 * "Total contributed" module: contribution x years, the same figure the
 * existing tax-savings calculation is based on (calculate.ts).
 */
export function totalContributed(values: HsaSavingsValues): number {
  return cents((values.contributionAmt ?? 0) * (values.years ?? 0));
}

/** Medical expenses over the whole period (expenses x years). */
export function totalMedicalExpenses(values: HsaSavingsValues): number {
  return cents((values.medicalExpenses ?? 0) * (values.years ?? 0));
}
